import { DndContext, DragOverlay, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { ModuleItemType, SubmoduleItem } from 'app/types/modules';
import { useTranslate } from 'core/hooks/useTranslate';
import Loading from '../../../atoms/Loading/Loading';
import Dialog from '../../../organisms/dialogs/Dialog/Dialog';
import DialogPhoto from 'core/modules/components/dialogs/DialogPhoto/DialogPhoto';
import { stripHtml } from 'core/utils/utils';
import React, { useMemo } from 'react';
import HeaderCourseSimple from 'core/modules/components/headers/HeaderCourseSimple/HeaderCourseSimple';
import AddModuleDialog from './components/AddModuleDialog/AddModuleDialog';
import DeleteModuleDialog from './components/DeleteModuleDialog/DeleteModuleDialog';
import EvaluationPeriodSection from './components/EvaluationPeriodSection';
import FloatingToggle from './components/FloatingToggle/FloatingToggle';
import ModuleRow from './components/ModuleRow';
import ReorderBanner from './components/ReorderBanner/ReorderBanner';
import SubmoduleRow from './components/SubmoduleRow';
import ToggleVisibilityDialog from './components/ToggleVisibilityDialog/ToggleVisibilityDialog';
import UnsavedChangesDialog from './components/UnsavedChangesDialog/UnsavedChangesDialog';
import useDialogOrganizeCourse from './useDialogOrganizeCourse';

type DialogOrganizeCourseProps = {
  open: boolean;
  courseGuid: string;
  programGuid: string;
  onClose: () => void;
};

function DialogOrganizeCourse({ open, courseGuid, programGuid, onClose }: DialogOrganizeCourseProps): JSX.Element {
  const { t } = useTranslate();
  const {
    reorderMode,
    periods,
    sensors,
    expandedPeriods,
    expandedModules,
    moduleIds,
    activeModule,
    activeSubmodule,
    activePeriodId,
    activeDragType,
    activeParentModuleGuid,
    addItemState,
    editItemState,
    courseName,
    classroomName,
    coursePicture,
    themeColor,
    gradient,
    themeImage,
    structureLevels,
    isDataLoading,
    isEdelvives,
    deleteItemState,
    toggleVisibilityState,
    isCreating,
    isEditing,
    isDeleting,
    isTogglingVisibility,
    hasPendingChanges,
    isSaving,
    photoState,
    setReorderMode,
    handleCloseDialog,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragCancel,
    handleSaveChanges,
    handleCancelChanges,
    togglePeriodExpansion,
    toggleModuleExpansion,
    handleOpenAddModule,
    handleOpenAddSubmodule,
    handleCloseAdd,
    handleOpenEdit,
    handleCloseEdit,
    handleOpenDelete,
    handleCloseDelete,
    handleOpenToggleVisibility,
    handleCloseToggleVisibility,
    handleConfirmToggleVisibility,
    handleConfirmAdd,
    handleConfirmEdit,
    handleConfirmDelete,
    showUnsavedChangesDialog,
    handleDiscardAndClose,
    handleSaveAndClose,
    handleCancelUnsavedChanges,
    handleOpenPhoto,
    handleClosePhoto,
    handlePhotoRefresh,
  } = useDialogOrganizeCourse({
    courseGuid,
    programGuid,
    onClose,
  });

  // Memoize empty array to avoid useEffect dependency warning in DndContext
  const emptySensors = useMemo(() => [], []);
  const activeSensors = reorderMode ? sensors : emptySensors;

  return (
    <Dialog
      isOpen={open}
      title={t('organize-content:Organize course')}
      isCloseButtonHidden
      onClose={handleCloseDialog}
      size="all-screen"
      className="dialog-organize-course"
      customHeader={
        <HeaderCourseSimple
          courseName={courseName}
          classroomName={classroomName ?? ''}
          coursePicture={coursePicture}
          themeColor={themeColor}
          gradient={gradient}
          themeImage={themeImage}
          onClose={handleCloseDialog}
        />
      }
    >
      <FloatingToggle reorderMode={reorderMode} onToggleMode={setReorderMode} hasOffset={hasPendingChanges} hasPendingChanges={hasPendingChanges} />
      {isDataLoading && <Loading isFullScreen={false} />}
      {!isDataLoading && (
        <div className="dialog-organize-course__content">
          {hasPendingChanges && <ReorderBanner onCancel={handleCancelChanges} onSave={handleSaveChanges} isLoading={isSaving} />}
          <DndContext
            sensors={activeSensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
            onDragCancel={handleDragCancel}
          >
            <SortableContext items={moduleIds} strategy={verticalListSortingStrategy}>
              <div className="dialog-organize-course__periods">
                {periods.map((period, index) => (
                  <EvaluationPeriodSection
                    key={period.id}
                    period={period}
                    structureLevels={structureLevels}
                    isEdelvives={isEdelvives}
                    isExpanded={expandedPeriods[period.id] ?? index === 0}
                    onToggleExpand={() => togglePeriodExpansion(period.id)}
                    expandedModules={expandedModules}
                    onToggleModuleExpansion={toggleModuleExpansion}
                    onAddModule={() => handleOpenAddModule(period.id)}
                    onAddSubmodule={handleOpenAddSubmodule}
                    activePeriodId={activePeriodId}
                    activeDragType={activeDragType}
                    activeParentModuleGuid={activeParentModuleGuid}
                    reorderMode={reorderMode}
                    onDelete={handleOpenDelete}
                    onEdit={handleOpenEdit}
                    onToggleVisibility={handleOpenToggleVisibility}
                    onOpenPhoto={handleOpenPhoto}
                  />
                ))}
              </div>
            </SortableContext>

            <DragOverlay>
              {activeModule && <ModuleRow module={activeModule} structureLevels={structureLevels} isDragOverlay reorderMode={reorderMode} />}
              {activeSubmodule && <SubmoduleRow submodule={activeSubmodule} isDragOverlay reorderMode={reorderMode} />}
            </DragOverlay>
          </DndContext>
        </div>
      )}

      {/* Add Module/Submodule Dialog */}
      {addItemState.isOpen && addItemState.type && (
        <AddModuleDialog isOpen={addItemState.isOpen} type={addItemState.type} onClose={handleCloseAdd} onConfirm={handleConfirmAdd} isLoading={isCreating} />
      )}

      {/* Edit Module/Submodule Dialog */}
      {editItemState.isOpen && editItemState.type && editItemState.item && (
        <AddModuleDialog
          isOpen={editItemState.isOpen}
          type={editItemState.type}
          onClose={handleCloseEdit}
          onConfirm={handleConfirmEdit}
          isLoading={isEditing}
          initialName={stripHtml(
            editItemState.type === 'module'
              ? (editItemState.item as ModuleItemType).lesson_name || (editItemState.item as ModuleItemType).section
              : (editItemState.item as SubmoduleItem).lesson_name
          )}
          mode="edit"
        />
      )}

      {/* Delete Module/Submodule Dialog */}
      {deleteItemState.isOpen && deleteItemState.type && (
        <DeleteModuleDialog
          isOpen={deleteItemState.isOpen}
          type={deleteItemState.type}
          onClose={handleCloseDelete}
          onSuccess={handleConfirmDelete}
          isLoading={isDeleting}
        />
      )}

      {/* Toggle Visibility Dialog */}
      {toggleVisibilityState.isOpen && toggleVisibilityState.type && toggleVisibilityState.item && (
        <ToggleVisibilityDialog
          isOpen={toggleVisibilityState.isOpen}
          type={toggleVisibilityState.type}
          isHidden={(toggleVisibilityState.item as ModuleItemType | SubmoduleItem).visibility === 0}
          onClose={handleCloseToggleVisibility}
          onSuccess={handleConfirmToggleVisibility}
          isLoading={isTogglingVisibility}
        />
      )}

      {/* Unsaved Changes Dialog */}
      {showUnsavedChangesDialog && (
        <UnsavedChangesDialog
          isOpen={showUnsavedChangesDialog}
          onClose={handleCancelUnsavedChanges}
          onDiscard={handleDiscardAndClose}
          onSaveAndClose={handleSaveAndClose}
          isSaving={isSaving}
        />
      )}

      {/* Photo Upload Dialog */}
      {photoState.isOpen && photoState.item && (
        <DialogPhoto
          open={photoState.isOpen}
          onRefresh={handlePhotoRefresh}
          program={{ guid: programGuid }}
          item={photoState.item}
          setOpen={(isOpen: boolean) => {
            if (!isOpen) handleClosePhoto();
          }}
          t={t}
        />
      )}
    </Dialog>
  );
}

export default DialogOrganizeCourse;
